/**
 * Near-Me Taxonomy (managed SSOT)
 * ===============================
 * Top-level CATEGORY -> SUBCATEGORY tree for the local-business directory.
 * This is the curated, versioned taxonomy: expanding it = publish a new
 * package version (no free-text categories/subcategories anywhere).
 *
 * `NEAR_ME_CATEGORY_SUBCATEGORIES` is the authoritative mapping; the flat
 * `NEAR_ME_SUBCATEGORY` object just gives every subcategory a stable
 * type + `z.enum` source. Keep both in sync when editing.
 */

/** Top-level business categories. */
export const NEAR_ME_CATEGORY = {
  FOOD_AND_DINING: 'food_and_dining',
  GROCERY_AND_ESSENTIALS: 'grocery_and_essentials',
  BEAUTY_AND_WELLNESS: 'beauty_and_wellness',
  HEALTH_AND_MEDICAL: 'health_and_medical',
  FITNESS_AND_SPORTS: 'fitness_and_sports',
  EDUCATION_AND_COACHING: 'education_and_coaching',
  PROFESSIONAL_SERVICES: 'professional_services',
  HOME_SERVICES: 'home_services',
  RETAIL_AND_SHOPPING: 'retail_and_shopping',
  AUTOMOTIVE: 'automotive',
  EVENTS_AND_PHOTOGRAPHY: 'events_and_photography',
  TRAVEL_AND_STAY: 'travel_and_stay',
  REPAIR_AND_ELECTRONICS: 'repair_and_electronics',
  REAL_ESTATE_SERVICES: 'real_estate_services',
  FINANCIAL_SERVICES: 'financial_services',
  OTHER: 'other',
} as const;
export type NearMeCategory = (typeof NEAR_ME_CATEGORY)[keyof typeof NEAR_ME_CATEGORY];

/** Flat map of every subcategory key -> slug (source for the type + z.enum). */
export const NEAR_ME_SUBCATEGORY = {
  // food_and_dining
  RESTAURANT: 'restaurant',
  CAFE: 'cafe',
  BAKERY: 'bakery',
  SWEET_SHOP: 'sweet_shop',
  FAST_FOOD: 'fast_food',
  CLOUD_KITCHEN: 'cloud_kitchen',
  TIFFIN_SERVICE: 'tiffin_service',
  ICE_CREAM_PARLOUR: 'ice_cream_parlour',
  JUICE_BAR: 'juice_bar',
  DHABA: 'dhaba',
  CATERING: 'catering',
  // grocery_and_essentials
  KIRANA_STORE: 'kirana_store',
  SUPERMARKET: 'supermarket',
  DAIRY_SHOP: 'dairy_shop',
  VEGETABLE_FRUIT_SHOP: 'vegetable_fruit_shop',
  GENERAL_STORE: 'general_store',
  ORGANIC_STORE: 'organic_store',
  MEAT_FISH_SHOP: 'meat_fish_shop',
  // beauty_and_wellness
  SALON: 'salon',
  SPA: 'spa',
  BEAUTY_PARLOUR: 'beauty_parlour',
  BARBER_SHOP: 'barber_shop',
  NAIL_STUDIO: 'nail_studio',
  MAKEUP_ARTIST: 'makeup_artist',
  MASSAGE_CENTRE: 'massage_centre',
  COSMETIC_CLINIC: 'cosmetic_clinic',
  // health_and_medical
  DOCTOR_CLINIC: 'doctor_clinic',
  HOSPITAL: 'hospital',
  DENTAL_CLINIC: 'dental_clinic',
  PHARMACY: 'pharmacy',
  DIAGNOSTIC_LAB: 'diagnostic_lab',
  PHYSIOTHERAPY: 'physiotherapy',
  OPTICAL_STORE: 'optical_store',
  VETERINARY_CLINIC: 'veterinary_clinic',
  AYURVEDIC_CLINIC: 'ayurvedic_clinic',
  MENTAL_HEALTH: 'mental_health',
  // fitness_and_sports
  GYM: 'gym',
  YOGA_STUDIO: 'yoga_studio',
  FITNESS_TRAINER: 'fitness_trainer',
  SPORTS_ACADEMY: 'sports_academy',
  DANCE_STUDIO: 'dance_studio',
  MARTIAL_ARTS: 'martial_arts',
  SWIMMING_POOL: 'swimming_pool',
  SPORTS_SHOP: 'sports_shop',
  // education_and_coaching
  TUITION_CLASSES: 'tuition_classes',
  COACHING_INSTITUTE: 'coaching_institute',
  SCHOOL: 'school',
  PLAYSCHOOL: 'playschool',
  COMPUTER_TRAINING: 'computer_training',
  MUSIC_CLASSES: 'music_classes',
  HOBBY_CLASSES: 'hobby_classes',
  LANGUAGE_CLASSES: 'language_classes',
  LIBRARY: 'library',
  SKILL_TRAINING: 'skill_training',
  // professional_services
  CHARTERED_ACCOUNTANT: 'chartered_accountant',
  LAWYER: 'lawyer',
  BUSINESS_CONSULTANT: 'business_consultant',
  ARCHITECT: 'architect',
  INTERIOR_DESIGNER: 'interior_designer',
  TAX_CONSULTANT: 'tax_consultant',
  NOTARY: 'notary',
  HR_RECRUITMENT: 'hr_recruitment',
  // home_services
  ELECTRICIAN: 'electrician',
  PLUMBER: 'plumber',
  CARPENTER: 'carpenter',
  PAINTER: 'painter',
  PEST_CONTROL: 'pest_control',
  HOME_CLEANING: 'home_cleaning',
  APPLIANCE_REPAIR: 'appliance_repair',
  AC_SERVICE: 'ac_service',
  PACKERS_MOVERS: 'packers_movers',
  MASON: 'mason',
  GARDENER: 'gardener',
  // retail_and_shopping
  CLOTHING_STORE: 'clothing_store',
  FOOTWEAR_STORE: 'footwear_store',
  JEWELLERY_STORE: 'jewellery_store',
  MOBILE_STORE: 'mobile_store',
  ELECTRONICS_STORE: 'electronics_store',
  FURNITURE_STORE: 'furniture_store',
  GIFT_SHOP: 'gift_shop',
  STATIONERY_STORE: 'stationery_store',
  HARDWARE_STORE: 'hardware_store',
  BOOK_STORE: 'book_store',
  TOY_STORE: 'toy_store',
  // automotive
  CAR_DEALER: 'car_dealer',
  BIKE_DEALER: 'bike_dealer',
  CAR_SERVICE: 'car_service',
  BIKE_SERVICE: 'bike_service',
  CAR_WASH: 'car_wash',
  TYRE_SHOP: 'tyre_shop',
  SPARE_PARTS: 'spare_parts',
  DRIVING_SCHOOL: 'driving_school',
  CAR_RENTAL: 'car_rental',
  // events_and_photography
  EVENT_PLANNER: 'event_planner',
  PHOTOGRAPHER: 'photographer',
  VIDEOGRAPHER: 'videographer',
  BANQUET_HALL: 'banquet_hall',
  DECORATOR: 'decorator',
  DJ_SERVICES: 'dj_services',
  TENT_HOUSE: 'tent_house',
  PARTY_SUPPLIES: 'party_supplies',
  // travel_and_stay
  HOTEL: 'hotel',
  GUEST_HOUSE: 'guest_house',
  HOMESTAY: 'homestay',
  TRAVEL_AGENCY: 'travel_agency',
  TOUR_OPERATOR: 'tour_operator',
  TAXI_SERVICE: 'taxi_service',
  BUS_OPERATOR: 'bus_operator',
  // repair_and_electronics
  MOBILE_REPAIR: 'mobile_repair',
  COMPUTER_REPAIR: 'computer_repair',
  WATCH_REPAIR: 'watch_repair',
  CCTV_INSTALLATION: 'cctv_installation',
  ELECTRONICS_REPAIR: 'electronics_repair',
  RO_WATER_SERVICE: 'ro_water_service',
  // real_estate_services
  PROPERTY_DEALER: 'property_dealer',
  BUILDER_DEVELOPER: 'builder_developer',
  RENTAL_AGENT: 'rental_agent',
  PG_HOSTEL: 'pg_hostel',
  PROPERTY_VALUATION: 'property_valuation',
  // financial_services
  INSURANCE_AGENT: 'insurance_agent',
  LOAN_AGENT: 'loan_agent',
  INVESTMENT_ADVISOR: 'investment_advisor',
  MONEY_TRANSFER: 'money_transfer',
  BANKING_CORRESPONDENT: 'banking_correspondent',
  // other
  OTHER_BUSINESS: 'other_business',
} as const;
export type NearMeSubcategory = (typeof NEAR_ME_SUBCATEGORY)[keyof typeof NEAR_ME_SUBCATEGORY];

/** Authoritative category -> subcategories mapping. */
export const NEAR_ME_CATEGORY_SUBCATEGORIES: Record<NearMeCategory, readonly NearMeSubcategory[]> =
  {
    food_and_dining: [
      'restaurant',
      'cafe',
      'bakery',
      'sweet_shop',
      'fast_food',
      'cloud_kitchen',
      'tiffin_service',
      'ice_cream_parlour',
      'juice_bar',
      'dhaba',
      'catering',
    ],
    grocery_and_essentials: [
      'kirana_store',
      'supermarket',
      'dairy_shop',
      'vegetable_fruit_shop',
      'general_store',
      'organic_store',
      'meat_fish_shop',
    ],
    beauty_and_wellness: [
      'salon',
      'spa',
      'beauty_parlour',
      'barber_shop',
      'nail_studio',
      'makeup_artist',
      'massage_centre',
      'cosmetic_clinic',
    ],
    health_and_medical: [
      'doctor_clinic',
      'hospital',
      'dental_clinic',
      'pharmacy',
      'diagnostic_lab',
      'physiotherapy',
      'optical_store',
      'veterinary_clinic',
      'ayurvedic_clinic',
      'mental_health',
    ],
    fitness_and_sports: [
      'gym',
      'yoga_studio',
      'fitness_trainer',
      'sports_academy',
      'dance_studio',
      'martial_arts',
      'swimming_pool',
      'sports_shop',
    ],
    education_and_coaching: [
      'tuition_classes',
      'coaching_institute',
      'school',
      'playschool',
      'computer_training',
      'music_classes',
      'hobby_classes',
      'language_classes',
      'library',
      'skill_training',
    ],
    professional_services: [
      'chartered_accountant',
      'lawyer',
      'business_consultant',
      'architect',
      'interior_designer',
      'tax_consultant',
      'notary',
      'hr_recruitment',
    ],
    home_services: [
      'electrician',
      'plumber',
      'carpenter',
      'painter',
      'pest_control',
      'home_cleaning',
      'appliance_repair',
      'ac_service',
      'packers_movers',
      'mason',
      'gardener',
    ],
    retail_and_shopping: [
      'clothing_store',
      'footwear_store',
      'jewellery_store',
      'mobile_store',
      'electronics_store',
      'furniture_store',
      'gift_shop',
      'stationery_store',
      'hardware_store',
      'book_store',
      'toy_store',
    ],
    automotive: [
      'car_dealer',
      'bike_dealer',
      'car_service',
      'bike_service',
      'car_wash',
      'tyre_shop',
      'spare_parts',
      'driving_school',
      'car_rental',
    ],
    events_and_photography: [
      'event_planner',
      'photographer',
      'videographer',
      'banquet_hall',
      'decorator',
      'dj_services',
      'tent_house',
      'party_supplies',
    ],
    travel_and_stay: [
      'hotel',
      'guest_house',
      'homestay',
      'travel_agency',
      'tour_operator',
      'taxi_service',
      'bus_operator',
    ],
    repair_and_electronics: [
      'mobile_repair',
      'computer_repair',
      'watch_repair',
      'cctv_installation',
      'electronics_repair',
      'ro_water_service',
    ],
    real_estate_services: [
      'property_dealer',
      'builder_developer',
      'rental_agent',
      'pg_hostel',
      'property_valuation',
    ],
    financial_services: [
      'insurance_agent',
      'loan_agent',
      'investment_advisor',
      'money_transfer',
      'banking_correspondent',
    ],
    other: ['other_business'],
  };

/** All subcategory slugs for a given category (empty array if unknown). */
export function subcategoriesForCategory(category: NearMeCategory): readonly NearMeSubcategory[] {
  return NEAR_ME_CATEGORY_SUBCATEGORIES[category] ?? [];
}

/** True when `subcategory` is a valid child of `category`. */
export function isSubcategoryOfCategory(
  category: NearMeCategory,
  subcategory: NearMeSubcategory,
): boolean {
  return subcategoriesForCategory(category).includes(subcategory);
}
